import './localized-findings.scss';

import React from 'react';

import { Label, LabelColor } from 'app/components/utils/labels/labels';
import {
  LocalizedFinding,
  Pain,
  Rash,
  Swelling
} from 'lib/models/diagnoses/localized-finding';
import { ClassList } from 'lib/utils/react/classlist';
import { LuFileEdit, LuX } from 'react-icons/lu';

export type LocalizedFindingListProps = {
  findings: LocalizedFinding[];
  isEditable?: boolean;
  isDisplaySensitivity?: boolean;
  isSingleColumn?: boolean;
  onEdit?: (localizedFinding: LocalizedFinding, index: number) => void;
  onDelete?: (localizedFinding: LocalizedFinding) => void;
};

export type LocalizedFindingItemProps = {
  index?: number;
  finding: LocalizedFinding;
  isEditable?: boolean;
  isDisplaySensitivity?: boolean;
  isSingleColumn?: boolean;
  onEdit?: (localizedFinding: LocalizedFinding, index: number) => void;
  onDelete?: (localizedFinding: LocalizedFinding) => void;
};

export const LocalizedFindingList: React.FC<LocalizedFindingListProps> = ({
  findings,
  isEditable = false,
  isDisplaySensitivity = true,
  isSingleColumn = false,
  onEdit,
  onDelete
}) => {
  if (findings.length === 0) {
    return null;
  }

  return (
    <div className="localized-finding-list">
      {findings.map((finding, index) => (
        <LocalizedFindingItem
          key={finding.key()}
          index={index}
          finding={finding}
          isEditable={isEditable}
          isDisplaySensitivity={isDisplaySensitivity}
          isSingleColumn={isSingleColumn}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export const LocalizedFindingItem: React.FC<LocalizedFindingItemProps> = ({
  index,
  finding,
  isEditable = false,
  isDisplaySensitivity = true,
  isSingleColumn = false,
  onEdit,
  onDelete
}) => {
  const classes = ClassList.of('localized-finding-item').add(
    'is-single-column',
    isSingleColumn
  );
  const type = finding.type.label;

  return (
    <div className={classes.names}>
      <div className="localized-finding-item-type">
        <span className="localized-finding-item-title">
          {type}
          {isDisplaySensitivity ? (
            <span className="subtle font-normal"> ({finding.sensitivity.label})</span>
          ) : null}
        </span>
        {isEditable ? (
          <div className="localized-finding-item-buttons">
            <a
              className="button button-xs button-secondary"
              onClick={() => onEdit && onEdit(finding, index ?? 0)}
              title="Edit localized finding"
            >
              <span>Edit</span>
              <LuFileEdit />
            </a>
            <a
              className="button button-xs button-secondary"
              onClick={() => onDelete && onDelete(finding)}
              title="Delete localized finding"
            >
              <LuX />
            </a>
          </div>
        ) : null}
      </div>
      <div className="localized-finding-item-content">
        <FindingsList title="Radiates" labels={finding.radiates} />
        {finding.isPain() ? <PainFindings finding={finding as Pain} /> : null}
        {finding.isSwelling() ? (
          <SwellingFindings finding={finding as Swelling} />
        ) : null}
        {finding.isRash() ? <RashFindings finding={finding as Rash} /> : null}
      </div>
    </div>
  );
};

export const PainFindings: React.FC<{ finding: Pain }> = ({ finding }) => {
  return (
    <>
      <FindingsList title="Onset" labels={finding.onset} />
      <FindingsList title="Pattern" labels={finding.time} />
      <FindingsList title="Provoked by" labels={finding.provoked} />
      <FindingsList title="Decreased by" labels={finding.decreased} />
      <FindingsList title="Quality" labels={finding.quality} />
      <FindingsList title="Severity" labels={finding.severity} />
    </>
  );
};

export const SwellingFindings: React.FC<{ finding: Swelling }> = ({ finding }) => {
  return (
    <>
      <FindingsList title="Onset" labels={finding.onset} />
      <FindingsList title="Pattern" labels={finding.time} />
      <FindingsList title="Provoked by" labels={finding.provoked} />
      <FindingsList title="Decreased by" labels={finding.decreased} />
    </>
  );
};

export const RashFindings: React.FC<{ finding: Rash }> = ({ finding }) => {
  return (
    <>
      <FindingsList title="Distribution" labels={finding.distribution} />
      <FindingsList title="Lesion type" labels={finding.lesionType} />
      <FindingsList title="Lesion configuration" labels={finding.lesionConfig} />
      <FindingsList title="Lesion surface" labels={finding.lesionSurface} />
      <FindingsList title="Color" labels={finding.color} />
      <FindingsList title="Associated symptom" labels={finding.associatedSymptom} />
      <FindingsList title="Palpation" labels={finding.palpation} />
    </>
  );
};

export const FindingsList: React.FC<{
  title: string;
  labels: { id: number | string; label: string }[];
  color?: LabelColor;
}> = ({ title, labels, color = 'blue' }) => {
  if (labels.length === 0) {
    return null;
  }

  return (
    <div className="localized-finding-item-group">
      <div className="localized-finding-item-group-title">{title}</div>
      <div className="localized-finding-item-group-labels">
        {labels.map(({ id, label }) => (
          <Label key={id} label={label} color={color} />
        ))}
      </div>
    </div>
  );
};
